import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Camera, ChevronDown, ChevronRight, Loader2, Shield,
  User as UserIcon, X, ArrowLeft, ArrowRight, Trash2, CheckSquare, Square,
} from 'lucide-react';
import { formatDateTime } from '../lib/dateFormat';

/**
 * Feb 2026 — Loading Pictures section, collapsible + grouped-by-loader.
 *
 * Behaviour:
 *   * Header always visible. Renders the label "Loading Pictures", a
 *     total-count pill, and an expand/collapse chevron. Whole header
 *     is the toggle target so a click anywhere on the row flips the
 *     panel — larger tap surface for touch users.
 *   * Body is collapsed by default. When expanded, photos are grouped
 *     by their credited LOADER (`attributed_to_name`) so ops can scan
 *     each staff member's contribution independently.
 *   * Each group shows the loader's name + per-group photo count as a
 *     subheading, with the group's thumbnails in a responsive grid
 *     below. Uploader ≠ loader gets a small "via admin" badge on the
 *     thumbnail so on-behalf uploads remain visually distinct.
 *   * Clicking any thumbnail opens a full-screen lightbox with
 *     prev / next controls (keyboard arrows + on-screen buttons),
 *     photo attribution (loader, uploader if different, timestamp),
 *     and a close (Esc) affordance.
 *   * On orders with zero photos the section renders in a very
 *     compact "0 photos" collapsed state — no visual noise, but the
 *     header still sits there so users know the feature exists.
 *
 * Props:
 *   - orderId (required): the order whose photos to fetch/build URLs.
 *   - photos (optional): pre-fetched list; skips the self-fetch.
 *   - API (required): API base for photo URLs and the fetch.
 *   - authHeaders (required): auth for the self-fetch.
 *   - testidPrefix (optional): data-testid stem, defaults to
 *     "loading-pictures-section".
 *   - defaultExpanded (optional, default false): initial open state.
 */
const LoadingPicturesSection = ({
  orderId,
  photos: photosProp,
  API,
  authHeaders,
  testidPrefix = 'loading-pictures-section',
  defaultExpanded = false,
  // Feb 2026 — super-admins get a trash icon per thumbnail to
  // permanently delete a photo (DB row + Drive file). Feb 2026 v8 —
  // widened: any warehouse user carrying the
  // `allow_delete_loading_photos` flag on their admin_users row also
  // sees the trash icon (super admin toggles it per-user).
  isSuperAdmin = false,
  canDelete = false,
  onPhotoDeleted,
}) => {
  const trashEnabled = !!(isSuperAdmin || canDelete);
  const [photos, setPhotos] = useState(photosProp || []);
  const [loading, setLoading] = useState(!photosProp);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  // file_id currently being deleted → true. Guards the trash button
  // so a double-tap can't fire two DELETEs against the same photo.
  const [deletingId, setDeletingId] = useState('');
  // Feb 2026 — bulk select mode. When ON, thumbnails show checkboxes
  // and the header exposes "Select all" + "Delete selected" controls.
  // Off by default so the day-to-day flow (one-off cross button)
  // stays uncluttered.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // fileId → objectURL. Populated lazily once the section is
  // expanded so we don't waste bandwidth on collapsed cards. Image
  // bytes are fetched via authenticated axios (the `<img src>` tag
  // itself can't attach a Bearer token, which is why the plain
  // endpoint URL used to render as a broken image showing only the
  // alt-text filename). We revoke every object URL on unmount / on
  // photos change so we don't leak memory.
  const [thumbUrls, setThumbUrls] = useState({});

  useEffect(() => {
    if (photosProp) setPhotos(photosProp);
  }, [photosProp]);

  useEffect(() => {
    if (photosProp) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await axios.get(
          `${API}/admin/orders/${orderId}/loading-photos`,
          { headers: authHeaders },
        );
        if (!cancelled) setPhotos(r.data?.photos || []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Could not load photos');
          setPhotos([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, photosProp]);

  // Fetch each photo's bytes via authenticated axios and expose them
  // via `URL.createObjectURL` so <img src> can render them. Kicks in
  // ONLY once the section is expanded — collapsed cards stay cheap.
  useEffect(() => {
    if (!expanded || photos.length === 0) return undefined;
    let cancelled = false;
    const createdUrls = [];
    (async () => {
      // Only fetch photos we haven't already loaded.
      const toFetch = photos.filter((p) => !thumbUrls[p.file_id]);
      if (!toFetch.length) return;
      const results = await Promise.all(
        toFetch.map(async (p) => {
          try {
            const r = await axios.get(
              `${API}/admin/orders/${orderId}/loading-photos/${p.file_id}`,
              { responseType: 'blob', headers: authHeaders },
            );
            const objectUrl = URL.createObjectURL(r.data);
            createdUrls.push(objectUrl);
            return [p.file_id, objectUrl];
          } catch {
            return [p.file_id, ''];
          }
        }),
      );
      if (cancelled) {
        // Revoke anything we created before the effect got cancelled.
        createdUrls.forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      setThumbUrls((prev) => {
        const next = { ...prev };
        results.forEach(([id, url]) => { if (url) next[id] = url; });
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [expanded, photos, orderId, API, authHeaders, thumbUrls]);

  // Revoke every object URL on unmount / when the photo set changes
  // so refreshing a page or navigating away doesn't leak blob memory.
  useEffect(() => () => {
    Object.values(thumbUrls).forEach((u) => {
      if (u) URL.revokeObjectURL(u);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group by credited loader for the sub-heading list. Preserves the
  // arrival order of loaders (first-seen wins) so the layout is
  // stable across refreshes.
  const groups = useMemo(() => {
    const map = new Map();
    photos.forEach((p) => {
      const key = p.attributed_to || p.attributed_to_name || p.uploaded_by || 'unknown';
      const name = p.attributed_to_name || p.uploaded_by_name || 'Unknown Loader';
      if (!map.has(key)) map.set(key, { key, name, photos: [] });
      map.get(key).photos.push(p);
    });
    return Array.from(map.values());
  }, [photos]);

  // Lightbox keyboard controls — attached only while open.
  const closeLightbox = useCallback(() => setLightboxIdx(-1), []);
  const nextLightbox = useCallback(() => {
    setLightboxIdx((i) => (i < 0 ? -1 : (i + 1) % photos.length));
  }, [photos.length]);
  const prevLightbox = useCallback(() => {
    setLightboxIdx((i) => (i < 0 ? -1 : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);

  // Feb 2026 — super-admin delete flow. Confirmation prompt →
  // DELETE → optimistic local remove + object URL revoke. Any
  // stray lightbox indices are clamped so a delete doesn't leave
  // the viewer on an out-of-bounds slot.
  const deletePhoto = useCallback(async (fileId) => {
    if (!fileId || deletingId) return;
    if (typeof window !== 'undefined'
      && !window.confirm('Permanently delete this loading picture? This removes it from the database AND from Google Drive.')) {
      return;
    }
    setDeletingId(fileId);
    try {
      await axios.delete(
        `${API}/admin/orders/${orderId}/loading-photos/${fileId}`,
        { headers: authHeaders },
      );
      // Revoke the object URL to free the browser blob cache.
      setThumbUrls((prev) => {
        if (!prev[fileId]) return prev;
        try { URL.revokeObjectURL(prev[fileId]); } catch (_e) { /* ignore */ }
        const { [fileId]: _drop, ...rest } = prev;
        return rest;
      });
      setPhotos((prev) => {
        const next = prev.filter((p) => p.file_id !== fileId);
        // Clamp lightbox — close if the removed photo was open, or
        // slide back a slot if we ran past the new end.
        setLightboxIdx((idx) => {
          if (idx < 0) return idx;
          const removedIdx = prev.findIndex((p) => p.file_id === fileId);
          if (removedIdx === idx) return -1;
          if (removedIdx < idx) return idx - 1;
          if (idx >= next.length) return -1;
          return idx;
        });
        return next;
      });
      if (onPhotoDeleted) onPhotoDeleted(fileId);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Delete failed';
      // Toast is optional; keep the fallback lightweight.
      if (typeof window !== 'undefined') window.alert(`Could not delete: ${msg}`);
    } finally {
      setDeletingId('');
    }
  }, [API, orderId, authHeaders, deletingId, onPhotoDeleted]);

  // Feb 2026 — bulk select helpers.
  const toggleSelect = useCallback((fileId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId); else next.add(fileId);
      return next;
    });
  }, []);
  const selectGroup = useCallback((groupPhotos) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const ids = groupPhotos.map((p) => p.file_id);
      const allSelected = ids.every((id) => next.has(id));
      ids.forEach((id) => {
        if (allSelected) next.delete(id); else next.add(id);
      });
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);

  const bulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (typeof window !== 'undefined'
      && !window.confirm(
        `Permanently delete ${ids.length} loading picture${ids.length === 1 ? '' : 's'}?\n\nThey will be removed from the database AND from Google Drive. This cannot be undone.`
      )) {
      return;
    }
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${orderId}/loading-photos/bulk-delete`,
        { file_ids: ids },
        { headers: authHeaders },
      );
      // Revoke any object URLs for the removed photos.
      setThumbUrls((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (next[id]) {
            try { URL.revokeObjectURL(next[id]); } catch (_e) { /* ignore */ }
            delete next[id];
          }
        });
        return next;
      });
      setPhotos((prev) => prev.filter((p) => !selectedIds.has(p.file_id)));
      setSelectedIds(new Set());
      setSelectMode(false);
      setLightboxIdx(-1);
      if (onPhotoDeleted) ids.forEach((id) => onPhotoDeleted(id));
      if (typeof window !== 'undefined' && (data?.drive_errors || []).length) {
        window.alert(
          `${data.deleted} photo(s) deleted from the database, but ${data.drive_errors.length} Drive file(s) could not be removed. Ops will retry later.`
        );
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Bulk delete failed';
      if (typeof window !== 'undefined') window.alert(`Could not delete: ${msg}`);
    } finally {
      setBulkDeleting(false);
    }
  }, [API, orderId, authHeaders, selectedIds, onPhotoDeleted]);

  useEffect(() => {
    if (lightboxIdx < 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') nextLightbox();
      else if (e.key === 'ArrowLeft') prevLightbox();
    };
    window.addEventListener('keydown', onKey);
    // Also lock body scroll while the lightbox is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIdx, closeLightbox, nextLightbox, prevLightbox]);

  const totalCount = photos.length;

  return (
    <div
      className="mt-3 rounded-lg border border-neutral-200 bg-white overflow-hidden"
      data-testid={testidPrefix}
    >
      {/* ---------- Header (always visible, click to toggle) ---------
          Feb 2026 — styled to be VISUALLY IDENTICAL to the
          ReceivedDocumentsPanel header (same chevron+icon+uppercase
          label+emerald pill layout) so the two cards read as siblings
          in the order detail view. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 transition text-left"
        aria-expanded={expanded}
        data-testid={`${testidPrefix}-toggle`}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-neutral-500 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-neutral-500 shrink-0" />
        )}
        <Camera className="w-4 h-4 text-emerald-600 shrink-0" />
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#0F172A]">
          Loading Pictures
        </div>
        <span
          className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 tabular-nums"
          data-testid={`${testidPrefix}-count`}
        >
          {loading ? '…' : totalCount}
        </span>
        {loading && (
          <Loader2 className="ml-auto w-3.5 h-3.5 text-neutral-400 animate-spin shrink-0" />
        )}
      </button>

      {/* ---------- Collapsible body ---------- */}
      {expanded && (
        <div
          className="border-t border-neutral-200 p-3"
          data-testid={`${testidPrefix}-body`}
        >
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading pictures…
            </div>
          ) : error ? (
            <div className="text-xs text-red-600" data-testid={`${testidPrefix}-error`}>
              {error}
            </div>
          ) : totalCount === 0 ? (
            <div
              className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50 py-6 text-center text-xs text-neutral-500"
              data-testid={`${testidPrefix}-empty`}
            >
              <Camera className="w-5 h-5 mx-auto mb-1.5 text-neutral-400" />
              No loading pictures uploaded yet for this order.
            </div>
          ) : (
            <div className="space-y-3" data-testid={`${testidPrefix}-groups`}>
              {trashEnabled && (
                <div
                  className="flex flex-wrap items-center gap-2 pb-2 border-b border-neutral-100"
                  data-testid={`${testidPrefix}-bulk-toolbar`}
                >
                  {!selectMode ? (
                    <button
                      type="button"
                      onClick={() => setSelectMode(true)}
                      className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 text-[11px] font-semibold px-2.5 py-1 text-neutral-700"
                      data-testid={`${testidPrefix}-select-mode-btn`}
                    >
                      <CheckSquare className="w-3.5 h-3.5" /> Select
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={exitSelectMode}
                        className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 text-[11px] font-semibold px-2.5 py-1 text-neutral-600"
                        data-testid={`${testidPrefix}-select-cancel-btn`}
                      >
                        <X className="w-3.5 h-3.5" /> Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const all = photos.map((p) => p.file_id);
                          const allSelected = all.every((id) => selectedIds.has(id));
                          setSelectedIds(allSelected ? new Set() : new Set(all));
                        }}
                        className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-white hover:bg-sky-50 text-[11px] font-semibold px-2.5 py-1 text-sky-700"
                        data-testid={`${testidPrefix}-select-all-btn`}
                      >
                        {photos.length > 0 && photos.every((p) => selectedIds.has(p.file_id))
                          ? 'Deselect all' : 'Select all'}
                      </button>
                      <span className="text-[11px] font-semibold text-neutral-500" data-testid={`${testidPrefix}-select-count`}>
                        {selectedIds.size} selected
                      </span>
                      {selectedIds.size > 0 && (
                        <button
                          type="button"
                          onClick={bulkDelete}
                          disabled={bulkDeleting}
                          className="ml-auto inline-flex items-center gap-1 rounded-full border border-red-600 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold px-3 py-1 disabled:opacity-60"
                          data-testid={`${testidPrefix}-bulk-delete-btn`}
                        >
                          {bulkDeleting
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Trash2 className="w-3.5 h-3.5" />}
                          Delete {selectedIds.size} selected
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
              {groups.map((g) => (
                <div key={g.key} data-testid={`${testidPrefix}-group-${g.key}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <UserIcon className="w-3.5 h-3.5 text-emerald-600" />
                    <div
                      className="text-xs font-bold text-neutral-800"
                      data-testid={`${testidPrefix}-group-name-${g.key}`}
                    >
                      {g.name}
                    </div>
                    <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-1.5 py-0.5 text-[10px] font-bold tabular-nums">
                      {g.photos.length}
                    </span>
                    {selectMode && trashEnabled && (
                      <button
                        type="button"
                        onClick={() => selectGroup(g.photos)}
                        className="ml-auto inline-flex items-center gap-1 rounded-full border border-sky-200 bg-white hover:bg-sky-50 text-[10px] font-semibold px-2 py-0.5 text-sky-700"
                        data-testid={`${testidPrefix}-group-select-all-${g.key}`}
                      >
                        {g.photos.every((p) => selectedIds.has(p.file_id))
                          ? 'Deselect group' : 'Select all'}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {g.photos.map((p) => {
                      const src = thumbUrls[p.file_id] || '';
                      const viaAdmin = p.uploader_role === 'admin_on_behalf';
                      const globalIdx = photos.findIndex((x) => x.file_id === p.file_id);
                      const isDeleting = deletingId === p.file_id;
                      const isSelected = selectedIds.has(p.file_id);
                      return (
                        <div key={p.file_id} className="relative group/photo">
                          <button
                            type="button"
                            onClick={() => {
                              if (selectMode && trashEnabled) {
                                toggleSelect(p.file_id);
                              } else {
                                setLightboxIdx(globalIdx);
                              }
                            }}
                            className={`w-full block group relative rounded-lg border overflow-hidden bg-white hover:border-[#0078D4] focus:outline-none focus:ring-2 focus:ring-[#0078D4] ${isSelected ? 'border-sky-500 ring-2 ring-sky-400' : 'border-neutral-200'}`}
                            data-testid={`${testidPrefix}-thumb-${p.file_id}`}
                            title={`Uploaded ${formatDateTime(p.uploaded_at)}${viaAdmin ? ` · via ${p.uploaded_by_name || 'admin'}` : ''}`}
                          >
                            <div className="aspect-square bg-neutral-100 flex items-center justify-center">
                              {src ? (
                                <img
                                  src={src}
                                  alt=""
                                  loading="lazy"
                                  className={`w-full h-full object-cover group-hover:scale-[1.02] transition-transform ${isSelected ? 'opacity-80' : ''}`}
                                />
                              ) : (
                                <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
                              )}
                            </div>
                            {viaAdmin && (
                              <span
                                className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 shadow-sm"
                                data-testid={`${testidPrefix}-badge-${p.file_id}`}
                              >
                                <Shield className="w-2.5 h-2.5" /> via admin
                              </span>
                            )}
                            <div className="px-2 py-1.5 text-[10px] text-neutral-500 tabular-nums text-left">
                              {formatDateTime(p.uploaded_at)}
                            </div>
                          </button>
                          {selectMode && trashEnabled && (
                            <span
                              aria-hidden="true"
                              className={`absolute top-1 left-1 inline-flex items-center justify-center w-7 h-7 rounded-md shadow-md ring-2 ring-white pointer-events-none ${isSelected ? 'bg-sky-600 text-white' : 'bg-white text-sky-600'}`}
                              data-testid={`${testidPrefix}-checkbox-${p.file_id}`}
                            >
                              {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                            </span>
                          )}
                          {trashEnabled && !selectMode && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (!window.confirm('Remove this loading picture?')) return;
                                deletePhoto(p.file_id);
                              }}
                              disabled={isDeleting}
                              aria-label="Remove loading picture"
                              title="Remove loading picture"
                              // Feb 2026 v9 — always-visible cross button on
                              // every thumbnail so warehouse staff on touch
                              // devices (no hover) can tap-remove a double
                              // upload straight from the Loading grid.
                              className="absolute top-1 right-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-600 text-white shadow-lg ring-2 ring-white hover:bg-red-700 active:scale-95 transition disabled:opacity-60 disabled:cursor-wait"
                              data-testid={`${testidPrefix}-delete-${p.file_id}`}
                            >
                              {isDeleting
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <X className="w-4 h-4" strokeWidth={3} />
                              }
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------- Full-screen lightbox ---------- */}
      {lightboxIdx >= 0 && photos[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
          data-testid={`${testidPrefix}-lightbox`}
          onClick={closeLightbox}
        >
          {/* Top bar with attribution + close */}
          <div
            className="flex items-start justify-between gap-3 px-4 py-3 bg-black/40 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0">
              <div className="text-sm font-bold truncate flex items-center gap-1.5">
                <UserIcon className="w-3.5 h-3.5" />
                {photos[lightboxIdx].attributed_to_name || 'Unknown Loader'}
              </div>
              {photos[lightboxIdx].uploader_role === 'admin_on_behalf' && (
                <div className="text-[11px] text-amber-300 mt-0.5 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  via {photos[lightboxIdx].uploaded_by_name || 'admin'}
                </div>
              )}
              <div className="text-[11px] text-white/70 mt-0.5 tabular-nums">
                {formatDateTime(photos[lightboxIdx].uploaded_at)}
                <span className="mx-2 text-white/40">·</span>
                {lightboxIdx + 1} / {photos.length}
              </div>
            </div>
            <button
              type="button"
              onClick={closeLightbox}
              className="p-2 rounded-full hover:bg-white/10 transition"
              aria-label="Close"
              data-testid={`${testidPrefix}-lightbox-close`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Image + prev/next */}
          <div
            className="flex-1 flex items-center justify-center relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {thumbUrls[photos[lightboxIdx].file_id] ? (
              <img
                src={thumbUrls[photos[lightboxIdx].file_id]}
                alt=""
                className="max-h-full max-w-full object-contain select-none"
                onClick={closeLightbox}
                data-testid={`${testidPrefix}-lightbox-image`}
              />
            ) : (
              <Loader2 className="w-8 h-8 text-white/60 animate-spin" />
            )}
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                  aria-label="Previous photo"
                  data-testid={`${testidPrefix}-lightbox-prev`}
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                  aria-label="Next photo"
                  data-testid={`${testidPrefix}-lightbox-next`}
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadingPicturesSection;
